import {
  query,
  where,
  getCountFromServer,
  getDocs,
  orderBy,
  limit,
} from "../../firebase";
import {
  getTicketsRef,
  getEventData,
  getUserData,
  getEventsRef,
  getUserEventsRef,
  getTicketsSnap,
} from "../../utils/FirebaseUtils";
import {
  Event,
  CustomTicketV2,
  PromoCode,
  PromoCodeType,
  AccountData,
  TicketV2,
  RequestStatus,
  OrganizerType,
  EventType,
} from "@markit/common.types";
import {
  filterUndefinedValues,
  handlePluralString,
  isEventTicketsPaid,
  isPaidTicketType,
  isProductSpecificType,
  uniqueVals,
} from "@markit/common.utils";
import { ErrorCode } from "@markit/common.api";
import { getUserRoleTicket, getUserTicket } from "./userTicketUtils";
import { API } from "../../API";
import { fetchSingleUser } from "../FetchSingleData";
import { withFirestoreTimeout } from "../firestoreTimeout";

/**
 * Get total attendee tickets
 */
export const getTotalAttendeeTickets = async (
  eventId: string
): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("uid", "!=", ""),
    where("role.type", "==", OrganizerType.ATTENDEE)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get total unique attendee tickets
 */
export const getTotalUniqueAttendees = async (
  eventId: string
): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("redeemedBy", "!=", ""),
    where("role.type", "==", OrganizerType.ATTENDEE)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get total attendee accepted tickets
 */
export const getTotalAcceptedAttendeeTickets = async (
  eventId: string
): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("uid", "!=", ""),
    where("requestStatus", "==", "accepted"),
    where("role.type", "==", OrganizerType.ATTENDEE)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get total unique accepted attendee tickets
 */
export const getTotalUniqueAcceptedAttendees = async (
  eventId: string
): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("redeemedBy", "!=", ""),
    where("requestStatus", "==", "accepted"),
    where("role.type", "==", OrganizerType.ATTENDEE)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get the number of event organizers
 */
export const getTotalOrganizers = async (eventId: string) => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(ticketsRef, where("customTicketId", "==", ""));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get the number of assigned event organizers
 */
export const getTotalAssignedOrganizers = async (eventId: string) => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("customTicketId", "==", ""),
    where("uid", "!=", "")
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get the total number of actual tickets sold (not including host distributed tickets)
 * Useful for tickets involving paid events
 */
export const getTotalTicketsSold = async (eventId: string) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("originalOwner", "!=", eventData.createdBy),
      where("requestStatus", "==", RequestStatus.ACCEPTED)
    );
    const snapshot = await getCountFromServer(query_);

    return snapshot.data().count;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get the total number of actual tickets sold with a certain custom ticket (not including host distributed tickets)
 * Useful for tickets involving paid events
 */
export const getTotalTicketsSoldInGroup = async (
  eventId: string,
  customTicketId: string,
  userId: string,
  excludePending?: boolean // true if you do not want to include request pending tickets sold for the request customTicketId
) => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = excludePending
    ? query(
        ticketsRef,
        where("customTicketId", "==", customTicketId),
        where("originalOwner", "!=", userId),
        where("requestStatus", "==", RequestStatus.ACCEPTED)
      )
    : query(
        ticketsRef,
        where("customTicketId", "==", customTicketId),
        where("originalOwner", "!=", userId)
      );
  const refundedTicketsQuery = query(
    ticketsRef,
    where("customTicketId", "==", customTicketId),
    where("requestStatus", "==", RequestStatus.REFUNDED)
  );

  const [snapshot, refundedTicketsSnapshot] = await Promise.all([
    getCountFromServer(query_),
    getCountFromServer(refundedTicketsQuery),
  ]);
  const refundedTickets = refundedTicketsSnapshot.data().count;

  return Math.max(snapshot.data().count - refundedTickets, 0);
};

/**
 * Get the total number of host distributed tickets
 * Useful for tickets involving paid events
 */
export const getTotalTicketsDistributed = async (eventId: string) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("originalOwner", "==", eventData.createdBy),
      where("customTicketId", "!=", "")
    );
    const snapshot = await getCountFromServer(query_);

    return snapshot.data().count;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get the total number of host distributed tickets with a certain custom ticket
 * Useful for tickets involving paid events
 */
export const getTotalTicketsDistributedInGroup = async (
  eventId: string,
  customTicketId: string
) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("customTicketId", "==", customTicketId),
      where("originalOwner", "==", eventData.createdBy)
    );
    const snapshot = await getCountFromServer(query_);

    return snapshot.data().count;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get the number of redeemed host distributed tickets
 * Useful for tickets involving paid events
 */
export const getRedeemedTicketsDistributed = async (eventId: string) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("originalOwner", "==", eventData.createdBy),
      where("uid", "!=", "")
    );
    const snapshot = await getCountFromServer(query_);

    const organizers = await getTotalAssignedOrganizers(eventId);
    return snapshot.data().count - organizers;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get the number of redeemed host distributed tickets with a certain custom ticket
 * Useful for tickets involving paid events
 */
export const getRedeemedTicketsDistributedInGroup = async (
  eventId: string,
  customTicketId: string
) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("customTicketId", "==", customTicketId),
      where("originalOwner", "==", eventData.createdBy),
      where("uid", "!=", "")
    );
    const snapshot = await getCountFromServer(query_);

    return snapshot.data().count;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get the number of unredeemed host distributed tickets
 * Useful for tickets involving paid events
 */
export const getUnredeemedTicketsDistributed = async (eventId: string) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("originalOwner", "==", eventData.createdBy),
      where("uid", "==", "")
    );
    const snapshot = await getCountFromServer(query_);

    return snapshot.data().count;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get the number of unredeemed host distributed tickets with a certain custom ticket
 * Useful for tickets involving paid events
 */
export const getUnredeemedTicketsDistributedInGroup = async (
  eventId: string,
  customTicketId: string
) => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("customTicketId", "==", customTicketId),
      where("originalOwner", "==", eventData.createdBy),
      where("uid", "==", "")
    );
    const snapshot = await getCountFromServer(query_);

    return snapshot.data().count;
  } else {
    console.log("could not fetch event data");
    return 0;
  }
};

/**
 * Get how many tickets remaining in custom ticket that can be sold
 */
export const getTicketsRemainingInGroup = async (
  customTicket: CustomTicketV2,
  event: Event
) => {
  if (customTicket.quantityAvailable === 0) {
    return -1;
  }
  const ticketsSold = await getTotalTicketsSoldInGroup(
    event.id,
    customTicket.id,
    event.createdBy
  );
  return Math.max(customTicket.quantityAvailable - ticketsSold, 0);
};
/**
 * Check if the current ticket is in stock
 */
export const ticketInStock = async (
  customTicket: CustomTicketV2,
  event: Event
) => {
  if (customTicket.quantityAvailable === 0) {
    return true;
  }
  const ticketsSold = await getTotalTicketsSoldInGroup(
    event.id,
    customTicket.id,
    event.createdBy
  );
  return ticketsSold < customTicket.quantityAvailable;
};

/**
 * Check if the event has no tickets remaining
 */
export const eventAtCapacity = async (
  event: Event,
  visibleTickets: CustomTicketV2[]
) => {
  // Timeout+retry protected so a transient stall recovers on retry. On a
  // persistent failure this throws so the caller keeps the page in the loading
  // state instead of rendering with an incorrect capacity.
  const ticketAvailabilityResults = await withFirestoreTimeout(() =>
    Promise.all(
      visibleTickets.map((customTicket) => ticketInStock(customTicket, event))
    )
  );
  return ticketAvailabilityResults.every((ticketAvailable) => !ticketAvailable);
};

/**
 * Get the status of the ticket
 */
export const atCapacityEventLabel = (
  event: Event,
  ticket?: CustomTicketV2
): string => {
  if (isProductSpecificType(event.eventType)) {
    return "Out of Stock";
  } else if (
    isEventTicketsPaid(event) ||
    (ticket && isPaidTicketType(ticket))
  ) {
    return "Sold Out";
  } else {
    return "At Capacity";
  }
};

/**
 * Get the label for the custom ticket
 */
export const getTicketLabels = async (
  ticket: CustomTicketV2,
  ticketsRemaining: number,
  eventType: EventType
) => {
  let labels: string[] = [];
  const isProduct = isProductSpecificType(eventType);

  if (ticket.showSpotsLeft) {
    if (ticket.quantityAvailable === 0) {
      return labels;
    }
    if (ticketsRemaining > 0) {
      labels = labels.concat(
        `${ticketsRemaining} ${
          isProduct
            ? "left in stock"
            : `${handlePluralString("spot", ticketsRemaining)} left`
        }`
      );
    }
  }

  return labels;
};

export const getTicketDescription = async (
  ticket: CustomTicketV2,
  ticketsRemaining: number,
  eventType: EventType
) => {
  const labels = await getTicketLabels(ticket, ticketsRemaining, eventType);
  const label = labels.join(".");
  return label;
};

export const applyPromoCode = (
  ticketPriceCents: number,
  promoCode: PromoCode
) => {
  switch (promoCode.type) {
    case PromoCodeType.DOLLARS_OFF: {
      if (promoCode.constants.length === 1) {
        return Math.max(ticketPriceCents - promoCode.constants[0], 0);
      }
      return undefined;
    }
    case PromoCodeType.PERCENT_OFF: {
      if (promoCode.constants.length === 1) {
        return (ticketPriceCents * (100 - promoCode.constants[0])) / 100;
      }
      return undefined;
    }
  }
};

/**
 * Get visible guest list to show on event cards and full event (filters out organizers)
 */
export const getVisibleGuestList = async (
  eventId: string,
  numToDisplay: number
): Promise<AccountData[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("redeemedBy", "!=", ""),
    where("requestStatus", "==", RequestStatus.ACCEPTED),
    orderBy("redeemedBy", "desc"),
    limit(numToDisplay)
  );
  const ticketsSnap = await getDocs(query_);
  const guests = await Promise.all(
    ticketsSnap.docs.map(async (doc) => {
      const ticket = doc.data();
      if (ticket.customTicketId === "") {
        return undefined;
      }
      const userData = await getUserData(ticket.uid);
      if (userData) {
        return userData;
      }
      return undefined;
    })
  );
  const definedGuests: AccountData[] = filterUndefinedValues(guests);
  return definedGuests;
};

/**
 * Determines if the user is an organizer (host, cohost, scanner, performer)
 */
export const isUserOrganizer = async (event: Event, uid: string) => {
  let isOrganizer = false;

  if (event.createdBy === uid) {
    isOrganizer = true;
    return isOrganizer;
  }

  const ticket = await getUserTicket(event.id, uid);
  if (ticket) {
    const role = ticket.role.type;
    if (
      role === OrganizerType.COHOST ||
      role === OrganizerType.PROMOTER ||
      role === OrganizerType.SCANNER
    ) {
      isOrganizer = true;
    }
  }

  return isOrganizer;
};

/**
 * Returns object if ticket alias is found
 * using numbers in FullEventTicketOptions
 */
export const findFreeTicketAlias = async (
  eventId: string,
  alias: string
): Promise<[TicketV2 | undefined, ErrorCode]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(ticketsRef, where("alias", "==", alias));
  const querySnapshot = await getDocs(query_);

  if (querySnapshot.docs.length === 0) {
    return [undefined, ErrorCode.INVALID_PROMO];
  }
  const userTicket = querySnapshot.docs.map((doc) => doc.data());

  if (userTicket.length > 1) {
    for (let i = 0; i < userTicket.length; i++) {
      if (userTicket[i].uid === "") {
        return [userTicket[i], ErrorCode.OK];
      }
    }
    // return 4;
    return [undefined, ErrorCode.REACHED_MAX_USE];
  }
  return [userTicket[0], ErrorCode.OK];
};

export const fetchAllUpcomingEvents = async () => {
  const date = new Date();

  const eventsQuery = query(
    getEventsRef(),
    orderBy("end", "desc"),
    where("end", ">=", date.toISOString())
  );

  const events = (await getDocs(eventsQuery)).docs.map((snap) => snap.data());
  return events;
};

/**
 * NOT USED CURRENTLY
 * Get the upcoming public events hosted by the specified user
 */
export const fetchUpcomingUserEvents = async (
  uid: string,
  isMyAccount: boolean
) => {
  if (isMyAccount) {
    const eventsQuery = query(
      getUserEventsRef(uid),
      where("end", ">=", new Date().toISOString()),
      orderBy("end", "asc"),
      limit(3)
    );
    const events = (await getDocs(eventsQuery)).docs.map((snap) => snap.data());
    return events;
  } else {
    const eventsQuery = query(
      getUserEventsRef(uid),
      where("end", ">=", new Date().toISOString()),
      orderBy("end", "asc"),
      limit(4)
    );
    const events = (await getDocs(eventsQuery)).docs.map((snap) => snap.data());
    return events;
  }
};

// fetch all of a creators events
export const fetchCreatorEvents = async (uid: string) => {
  const events = await API.user.creatorEvents({ uid }).then((resp) => {
    const { events, errorFetching } = resp;
    if (errorFetching) {
      console.error("Error fetching events on initial app load");
      return [];
    }
    return events;
  });
  return events;
};

/**
 * Get total attendees that have been scanned
 */
export const getTotalScanned = async (eventId: string): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(ticketsRef, where("scanned", "==", true));
  const snapshot = await getCountFromServer(query_);

  return snapshot.data().count;
};

// Quick check for if the event has revenue
export const checkEventHasRevenue = async (eventId: string) => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("chargeId", "!=", ""),
    where("requestStatus", "==", RequestStatus.ACCEPTED)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count > 0;
};

/**
 * Gets the accumulated revenue for the selected event
 * Also a backend function for calculating revenue but this function determines revenue without calling stripe api
 */
export const getTotalRevenueForEvent = async (
  eventId: string
): Promise<number> => {
  const ticketsSnap = await getTicketsSnap(eventId);
  const tickets = ticketsSnap.docs.map((doc) => doc.data());

  const liveTickets = tickets.filter(
    (ticket) => ticket.requestStatus === RequestStatus.ACCEPTED
  );

  const uniqueChargeIds = new Set(
    liveTickets.map((ticket) => ticket.chargeId).filter(Boolean)
  );

  const totalRevenue = Array.from(uniqueChargeIds).reduce((prev, chargeId) => {
    const chargeTickets = tickets.filter((tix) => tix.chargeId === chargeId);
    const numChargeTickets = chargeTickets.length;
    const numRemainingTickets = chargeTickets.filter(
      (tix) => tix.requestStatus === RequestStatus.ACCEPTED
    ).length;
    const foundTicket = chargeTickets.find((tix) => tix.amountPaid > 0);

    if (foundTicket && numChargeTickets > 0) {
      return (
        prev + (foundTicket.amountPaid / numChargeTickets) * numRemainingTickets
      );
    }
    return prev;
  }, 0);
  return totalRevenue;
};

/**
 * Gets the accumulated tips for the selected event, kept separate from ticket revenue
 * so a host can read tips directly without computing them by hand for staff splits.
 */
export const getTotalTipsForEvent = async (eventId: string): Promise<number> => {
  const ticketsSnap = await getTicketsSnap(eventId);
  const tickets = ticketsSnap.docs.map((doc) => doc.data());

  const liveTickets = tickets.filter(
    (ticket) => ticket.requestStatus === RequestStatus.ACCEPTED
  );

  const uniqueChargeIds = new Set(
    liveTickets.map((ticket) => ticket.chargeId).filter(Boolean)
  );

  const totalTips = Array.from(uniqueChargeIds).reduce((prev, chargeId) => {
    const chargeTickets = tickets.filter((tix) => tix.chargeId === chargeId);
    const foundTicket = chargeTickets.find((tix) => tix.tipAmount > 0);
    return foundTicket ? prev + foundTicket.tipAmount : prev;
  }, 0);
  return totalTips;
};

/**
 * Get total redeemed by tickets including those that were redeemed by the host
 */
export const getTotalRedeemed = async (eventId: string): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("redeemedBy", "!=", ""),
    where("requestStatus", "==", RequestStatus.ACCEPTED)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get number of tickets sold by a promoter
 * Use this when you only have the uid of the user and need to get the promoter ticketId first.
 */
export const getTotalRevShareTicketsSoldByUser = async (
  eventId: string,
  promoterUid: string
): Promise<number> => {
  const promoterTicket = await getUserRoleTicket(eventId, promoterUid);

  if (promoterTicket) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("promoter", "==", promoterTicket.id),
      where("requestStatus", "==", RequestStatus.ACCEPTED)
    );
    const snapshot = await getCountFromServer(query_);
    return snapshot.data().count;
  } else {
    return 0;
  }
};

/**
 * Get number of tickets sold by a rev share organizer.
 * Use this when you already know the ticketId related to the rev share organizer.
 */
export const getTotalRevShareTicketsSoldByTicket = async (
  eventId: string,
  promoterTicketId: string
): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("promoter", "==", promoterTicketId),
    where("requestStatus", "==", RequestStatus.ACCEPTED)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get the total ticket revenue related to a promoter
 * Returns both the total revenue from the promoter tickets and the revenue split to the promoter
 */
export const getTotalPromoterRevenue = async (
  eventId: string,
  promoterUid: string
): Promise<{ totalRevenue: number; promoterRevenue: number }> => {
  const promoterTicket = await getUserRoleTicket(eventId, promoterUid);

  if (promoterTicket) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      where("promoter", "==", promoterTicket.id)
    );
    const querySnapshot = await getDocs(query_);
    const totalRevenue = querySnapshot.docs.reduce((acc, doc) => {
      const ticket = doc.data();
      return acc + ticket.amountPaid / 100;
    }, 0);
    const promoterRevenue = querySnapshot.docs.reduce((acc, doc) => {
      const ticket = doc.data();
      // If there is a chargeId and amountPaid is 0, this means it's an additional ticket on a purchase and so we want to use the rev dollar split vs the min, where amountPaid can be 0
      const finalRevDollarSplit = ticket.chargeId
        ? ticket.amountPaid > 0
          ? Math.min(promoterTicket.role.revenueDollarSplit, ticket.amountPaid)
          : promoterTicket.role.revenueDollarSplit
        : 0;
      const promoterAmount: number =
        (promoterTicket.role.revenuePercentSplit
          ? (ticket.amountPaid * promoterTicket.role.revenuePercentSplit) / 100
          : finalRevDollarSplit) / 100;
      return acc + promoterAmount;
    }, 0);

    return { totalRevenue, promoterRevenue };
  } else {
    return { totalRevenue: 0, promoterRevenue: 0 };
  }
};

// Returns the user data for the promoters and cohosts with revenue sharing enabled
export const getPromotersUserData = async (
  eventId: string,
  followingAccountData: AccountData[]
): Promise<AccountData[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const [percentRevDocs, dollarRevDocs] = await Promise.all([
    getDocs(query(ticketsRef, where("role.revenuePercentSplit", ">", 0))),
    getDocs(query(ticketsRef, where("role.revenueDollarSplit", ">", 0))),
  ]);
  const organizerTickets = [...percentRevDocs.docs, ...dollarRevDocs.docs];
  const promotersUserData = await Promise.all(
    organizerTickets.map(async (doc) => {
      const ticket = doc.data();
      if (ticket.uid === "") {
        return undefined;
      }
      return fetchSingleUser(ticket.uid, followingAccountData);
    })
  );
  return filterUndefinedValues(promotersUserData);
};

// Gets the total number of free ticket promo codes for an event
export const getTotalNumFreePromoTickets = async (
  eventId: string
): Promise<number> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(ticketsRef, where("alias", "!=", ""));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

/**
 * Get the userIds from all the tickets for an Event of a specific ticket type
 * Exclude the manual redeemed and excludedList people
 */
export const getUidsFromEventTicketType = async (
  eventId: string,
  customTicketId: string
): Promise<string[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("customTicketId", "==", customTicketId),
    where("uid", "!=", "")
  );
  const querySnapshot = await getDocs(query_);

  if (querySnapshot.docs.length === 0) {
    return [];
  }
  const tickets = querySnapshot.docs
    .map((doc) => doc.data())
    .filter(
      (ticket: TicketV2) => ticket.requestStatus !== RequestStatus.REFUNDED
    );

  return uniqueVals(tickets, (ticket: TicketV2) => ticket.uid).map(
    (ticket) => ticket.uid
  );
};

/**
 * Get the ticketData for all the tickets for an event
 */
export const fetchAllEventTickets = async (
  eventId: string
): Promise<TicketV2[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("uid", "!=", ""),
    where("role.type", "==", OrganizerType.ATTENDEE)
  );
  const querySnapshot = await getDocs(query_);

  if (querySnapshot.docs.length === 0) {
    console.log("no tickets found for: ", eventId);
    return [];
  }
  const tickets = querySnapshot.docs.map((doc) => doc.data());
  return tickets;
};

/**
 * Get the ticketData from all the unique accepted attendee tickets for an Event
 */
export const fetchUniqueEventTickets = async (
  eventId: string
): Promise<TicketV2[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(
    ticketsRef,
    where("redeemedBy", "!=", ""),
    where("role.type", "==", OrganizerType.ATTENDEE)
  );
  const querySnapshot = await getDocs(query_);
  if (querySnapshot.empty) {
    console.log("no tickets found for: ", eventId);
    return [];
  }
  const tickets = querySnapshot.docs.map((doc) => doc.data());
  return tickets;
};

/**
 * Get the ticket data from all specified status unique attendees for an event
 */
export const fetchEventAttendeeStatusTickets = async (
  eventId: string,
  status: RequestStatus
): Promise<TicketV2[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(ticketsRef, where("requestStatus", "==", status));
  const snapshot = await getDocs(query_);

  if (snapshot.docs.length === 0) {
    console.log("no tickets found for: ", eventId);
    return [];
  }

  const tickets = uniqueVals(
    snapshot.docs.map((doc) => doc.data()),
    (ticket: TicketV2) => ticket.uid
  );
  return tickets;
};

/**
 * Returs ALL Free Tickets with Alias Array
 */
export const fetchAllFreeTicketsWithAlias = async (
  eventId: string
): Promise<TicketV2[]> => {
  const eventData = await getEventData(eventId);
  if (eventData) {
    const ticketsRef = getTicketsRef(eventId);
    const query_ = query(
      ticketsRef,
      // TODO: if we add aliases to organizer ticket's this will also fetch organizer tickets
      // and break the function's purpose :)
      where("originalOwner", "==", eventData.createdBy),
      where("alias", "!=", "")
    );
    const querySnapshot = await getDocs(query_);

    if (querySnapshot.docs.length === 0) {
      console.log("Empty query snapshot");
      return [];
    } else {
      const freeTickets = querySnapshot.docs.map((doc) => doc.data());
      return freeTickets;
    }
  } else {
    console.log("could not fetch event data");
    return [];
  }
};

// Given an array of event organizers, return the array so that the host is the first element
export const getEventDisplayedOrganizers = (
  hostId: string,
  organizers: AccountData[]
) => {
  const finalOrganizers = organizers;
  const foundHost = organizers.find((organizer) => organizer.uid === hostId);
  const foundHostIndex = organizers.findIndex(
    (organizer) => organizer.uid === hostId
  );
  if (foundHostIndex !== -1 && foundHost) {
    finalOrganizers.splice(foundHostIndex, 1);
    finalOrganizers.unshift(foundHost);
  }
  return finalOrganizers;
};

export const fetchEventOrganizers = async (events: Event[]) => {
  const organizersArr = await Promise.all(
    events.map((event) => {
      return event.cohosts.concat(event.createdBy);
    })
  );
  const organizers = uniqueVals(organizersArr.flatMap((subArray) => subArray));
  const organizersData = await Promise.all(
    organizers.map(async (organizer) => {
      const userData = await getUserData(organizer);
      if (userData) {
        return userData;
      }
    })
  );
  const filteredOrganizersData: AccountData[] =
    filterUndefinedValues(organizersData);

  return filteredOrganizersData;
};

// Get user data for all tickets that have a uid
// We query by uid (not redeemedBy) because request tickets have empty redeemedBy.
export const fetchAllTicketUserData = async (
  eventId: string,
  followingAccountData: AccountData[]
): Promise<AccountData[]> => {
  const ticketsRef = getTicketsRef(eventId);
  const query_ = query(ticketsRef, where("uid", "!=", ""));
  const querySnapshot = await getDocs(query_);
  const tickets = querySnapshot.docs.map((doc) => doc.data());
  const uniqueUids = Array.from(
    new Set(tickets.map((t) => t.uid).filter(Boolean))
  );
  const userData = await Promise.all(
    uniqueUids.map((uid) => fetchSingleUser(uid, followingAccountData))
  );
  return filterUndefinedValues(userData);
};
